import React, { useEffect, useMemo, useState } from 'react';
import { SectionHeader, Callout } from '../components/UI';
import AiMarkdown from '../components/AiMarkdown';
import { useScenario } from '../context/ScenarioContext';
import { api, formatApiErrorDetail } from '../lib/api';
import { CircleDollarSign, Loader2, History as HistoryIcon } from 'lucide-react';
import { toast } from 'sonner';

const RISK_OPTIONS = [
  { value: 'conservative', label: 'Conservative — protect cash at all costs' },
  { value: 'moderate', label: 'Moderate — balanced growth + personal cash flow' },
  { value: 'aggressive', label: 'Aggressive — maximize take-home, re-up reserves from wins' },
];

export default function OwnersPay() {
  const { active, scenarios } = useScenario();
  const activeScenarios = useMemo(() => scenarios.filter((s) => !s.archived), [scenarios]);
  const [form, setForm] = useState({
    scenario_id: active?.id || '',
    target_take_home: 220000,
    risk_tolerance: 'moderate',
    min_cash_buffer: 200000,
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (active?.id && !form.scenario_id) setForm((f) => ({ ...f, scenario_id: active.id }));
  }, [active, form.scenario_id]);

  const loadHistory = () => api.get('/ai/analyses', { params: { kind: 'owners_pay', limit: 10 } }).then((r) => setHistory(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadHistory(); }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await api.post('/ai/owners-pay', {
        ...form,
        target_take_home: Number(form.target_take_home) || 0,
        min_cash_buffer: Number(form.min_cash_buffer) || 0,
      });
      setResult(data);
      loadHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-8">
      <SectionHeader
        eyebrow="Owner's Pay Optimizer · Claude"
        title="What should you pay yourself?"
        description="Answer three questions. Claude recommends a salary / draw / retained-profit split using your live scenario, flags the S-corp reasonable-compensation rule, and hands you a 12-month draw schedule to bring to your CPA."
      />

      <Callout title="Not tax advice" tone="default">
        This is a pressure-test tool, not a CPA. Always validate any change with your accountant before adjusting payroll or distributions.
      </Callout>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.3fr] gap-8">
        <form onSubmit={submit} className="bg-white border-t-4 border-indie-evergreen p-6 space-y-4" data-testid="pay-form">
          <div>
            <label className="label-editorial">Scenario</label>
            <select
              value={form.scenario_id}
              onChange={(e) => update('scenario_id', e.target.value)}
              className="mt-1 w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm"
              data-testid="pay-scenario"
            >
              {activeScenarios.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label-editorial">Target annual personal take-home</label>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-indie-ink/50">$</span>
              <input
                type="number"
                step="1000"
                value={form.target_take_home}
                onChange={(e) => update('target_take_home', e.target.value)}
                className="w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm num-tabular text-right"
                data-testid="pay-target"
              />
              <span className="text-xs text-indie-ink/50 uppercase tracking-wider">USD / year</span>
            </div>
            <div className="text-[11px] text-indie-ink/50 mt-1">Salary + owner draws landing in your personal bank account (pre-personal-tax).</div>
          </div>

          <div>
            <label className="label-editorial">Risk tolerance</label>
            <select
              value={form.risk_tolerance}
              onChange={(e) => update('risk_tolerance', e.target.value)}
              className="mt-1 w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm"
              data-testid="pay-risk"
            >
              {RISK_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>

          <div>
            <label className="label-editorial">Minimum business cash buffer</label>
            <div className="mt-1 flex items-center gap-2">
              <span className="text-xs text-indie-ink/50">$</span>
              <input
                type="number"
                step="5000"
                value={form.min_cash_buffer}
                onChange={(e) => update('min_cash_buffer', e.target.value)}
                className="w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm num-tabular text-right"
                data-testid="pay-buffer"
              />
              <span className="text-xs text-indie-ink/50 uppercase tracking-wider">USD</span>
            </div>
            <div className="text-[11px] text-indie-ink/50 mt-1">Floor the business must never dip below. Default: $200K safety floor.</div>
          </div>

          <div>
            <label className="label-editorial">Anything else to factor in?</label>
            <textarea
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              rows={3}
              placeholder="Planning a big coaching investment in Q3. New baby due in August."
              className="mt-1 w-full bg-white border border-indie-navy/20 px-3 py-2 text-sm"
              data-testid="pay-notes"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="pay-submit"
            className="w-full flex items-center justify-center gap-2 bg-indie-yellow text-indie-ink font-semibold uppercase tracking-wider text-sm px-4 py-3 hover:bg-indie-yellow/85 disabled:opacity-60"
          >
            {submitting ? <><Loader2 size={16} className="animate-spin" /> Claude is thinking…</> : <><CircleDollarSign size={16} /> Recommend a split</>}
          </button>
        </form>

        <div className="space-y-6">
          {submitting && (
            <Callout title="Claude is crunching the numbers" tone="default">
              Usually takes 20-45 seconds. Pulling in your current scenario, 12-month forecast, actuals, and cash trajectory.
            </Callout>
          )}

          {result && (
            <div className="bg-white border-t-4 border-indie-evergreen p-6" data-testid="pay-result">
              <div className="label-editorial text-indie-evergreen">Recommendation · {new Date(result.created_at).toLocaleString()}</div>
              <div className="text-xl font-semibold text-indie-navy mt-1 mb-4">
                ${Number(result.target_take_home).toLocaleString()} target take-home · {result.risk_tolerance}
              </div>
              <AiMarkdown>{result.narrative}</AiMarkdown>
            </div>
          )}

          {!submitting && !result && (
            <div className="bg-indie-cream border-t-4 border-indie-navy p-6">
              <div className="label-editorial">How this works</div>
              <ul className="mt-3 space-y-2 text-sm text-indie-ink/80 list-disc pl-5">
                <li>Tell Claude what you want to take home personally and how much cushion the business needs.</li>
                <li>Claude pulls your active scenario, BHAG, recent actuals, team size, and cash position.</li>
                <li>You get a recommended salary/draw/retained-profit split, the reasoning behind it, S-corp reasonable-comp flags, a 12-month draw schedule, and questions for your CPA.</li>
                <li>Run a few variations. Try conservative vs aggressive. Try different take-home targets. The goal is clarity, not a final answer.</li>
              </ul>
            </div>
          )}

          {history.length > 0 && (
            <div className="bg-white border-t-4 border-indie-navy p-6" data-testid="pay-history">
              <div className="flex items-center gap-2 label-editorial"><HistoryIcon size={14} /> Past recommendations</div>
              <div className="mt-3 space-y-2">
                {history.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => setResult(h)}
                    className="w-full text-left px-3 py-2 hover:bg-indie-cream transition-colors border-l-2 border-transparent hover:border-indie-evergreen"
                  >
                    <div className="text-sm font-medium text-indie-navy">
                      ${Number(h.target_take_home).toLocaleString()} take-home · {h.risk_tolerance}
                    </div>
                    <div className="text-xs text-indie-ink/60">{new Date(h.created_at).toLocaleString()} · {h.scenario_name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
